import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  ShoppingBag,
  Truck,
  RotateCcw,
  ShieldCheck,
  Zap,
  HeartPulse,
  ChevronDown,
  Headphones,
  Mail,
  ArrowRight,
  HelpCircle
} from 'lucide-react';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestionIndex, setOpenQuestionIndex] = useState(0); // Open 1st question by default

  const categories = [
    { id: 'general', label: 'General', icon: MessageSquare },
    { id: 'orders', label: 'Orders & Payments', icon: ShoppingBag },
    { id: 'shipping', label: 'Shipping & Delivery', icon: Truck },
    { id: 'returns', label: 'Returns & Refunds', icon: RotateCcw },
    { id: 'warranty', label: 'Warranty', icon: ShieldCheck },
    { id: 'product', label: 'Product & Usage', icon: Zap },
    { id: 'safety', label: 'Safety & Health', icon: HeartPulse }
  ];

  const faqData = {
    general: [
      {
        q: 'What is red light therapy?',
        a: 'Red light therapy uses specific wavelengths of red and near-infrared light (660nm & 850nm) to penetrate the skin and support cellular energy, reduce inflammation, promote recovery, and improve overall wellness.'
      },
      {
        q: 'How does InfraEdge red light therapy work?',
        a: 'InfraEdge devices deliver concentrated light energy directly to your cells’ mitochondria, stimulating ATP production and enhancing cellular repair and regeneration.'
      },
      {
        q: 'What are the benefits of red light therapy?',
        a: 'Key benefits include faster muscle recovery, reduced joint pain & stiffness, enhanced skin tone & collagen production, improved sleep quality, and boosted energy levels.'
      },
      {
        q: 'Is red light therapy safe?',
        a: 'Yes, red light therapy is 100% natural, non-invasive, drug-free, and chemical-free. It produces no harmful UV rays and no excessive heat.'
      },
      {
        q: 'How often should I use red light therapy?',
        a: 'We recommend 10 to 20 minute sessions, 3 to 5 times per week for optimal wellness and recovery results.'
      },
      {
        q: 'Can I use red light therapy on my face?',
        a: 'Yes, red light therapy is safe and beneficial for facial skin health. Protective eyewear is included with all InfraEdge panels for comfort.'
      },
      {
        q: 'How long does it take to see results?',
        a: 'Many users feel enhanced muscle relaxation immediately, while visible skin improvements and deep tissue recovery typically manifest within 2 to 4 weeks of consistent use.'
      }
    ],
    orders: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept major Credit/Debit cards (Visa, MasterCard, American Express), PayPal, Apple Pay, Google Pay, and Shop Pay.'
      },
      {
        q: 'Can I cancel or modify my order after placing it?',
        a: 'We process orders quickly. If you need to make changes, please contact our support team immediately before the shipment is dispatched.'
      },
      {
        q: 'Will I receive an invoice for my purchase?',
        a: 'Yes, a full PDF VAT invoice is automatically generated and emailed to you as soon as your order is placed.'
      }
    ],
    shipping: [
      {
        q: 'How long does shipping take?',
        a: 'Standard UK delivery takes 2 to 3 business days. All shipments are fully tracked via reliable courier partners.'
      },
      {
        q: 'Do you offer free shipping?',
        a: 'Yes, free tracked courier shipping is applied automatically to all orders exceeding our free shipping threshold.'
      },
      {
        q: 'How can I track my package?',
        a: 'You can track your order status in real time on our Shipping & Tracking page using your Order Number and Email Address.'
      }
    ],
    returns: [
      {
        q: 'What is your return policy?',
        a: 'We offer a 60-day money-back guarantee. If you are not satisfied, you can return your device in original condition within 60 days of delivery.'
      },
      {
        q: 'How do I start a return?',
        a: 'Contact our support team with your order number, and we will provide you with a prepaid return shipping label.'
      }
    ],
    warranty: [
      {
        q: 'What warranty comes with InfraEdge panels?',
        a: 'All InfraEdge panels come with a comprehensive 2-Year Manufacturer Warranty covering manufacturing defects and electrical components.'
      },
      {
        q: 'How do I submit a warranty claim?',
        a: 'Simply email our support team at Info@infraedgeredlight.co.uk with your order number and photos/videos of the issue.'
      }
    ],
    product: [
      {
        q: 'What is the difference between 660nm and 850nm wavelengths?',
        a: '660nm Red Light is absorbed primarily by skin tissue to enhance collagen and skin health, whereas 850nm Near-Infrared Light penetrates deeper into muscles and joints for recovery.'
      },
      {
        q: 'Can I use both 660nm and 850nm at the same time?',
        a: 'Yes, all InfraEdge panels allow independent or simultaneous dual-wavelength operation.'
      }
    ],
    safety: [
      {
        q: 'Are protective glasses required?',
        a: 'While red light is safe, near-infrared light is bright to sensitive eyes. We include protective goggles with every panel for maximum comfort.'
      },
      {
        q: 'Are there any side effects?',
        a: 'Red light therapy is non-invasive and safe with no known serious side effects when used according to guidelines.'
      }
    ]
  };

  const currentQuestions = faqData[activeCategory] || [];

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>FAQ</span>
        </div>

        {/* HERO HEADER WITH 3D FAQ & CHAT BUBBLE EMBLEM */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center', marginBottom: '48px' }}>
          <div>
            <h1 style={{ fontSize: '44px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Frequently Asked Questions
            </h1>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', borderRadius: '2px', marginBottom: '20px' }} />
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, maxWidth: '520px', margin: 0 }}>
              Find answers to the most common questions about InfraEdge red light therapy devices, orders, shipping, returns, warranty and more.
            </p>
          </div>

          {/* Right 3D Red FAQ Emblem Graphic */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '72px', fontWeight: 900, color: '#dc2626', letterSpacing: '-0.04em', lineHeight: 1, textShadow: '0 10px 25px rgba(220,38,38,0.2)' }}>
                FAQ
              </div>
              <div style={{ width: '54px', height: '44px', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '16px 16px 16px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', boxShadow: '0 8px 20px rgba(0,0,0,0.04)' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626' }} />
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626' }} />
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626' }} />
              </div>
            </div>
          </div>
        </div>

        {/* MAIN SIDEBAR CATEGORIES + ACCORDION QUESTIONS LAYOUT */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '32px', alignItems: 'flex-start', marginBottom: '48px' }}>
          
          {/* Left Category Sidebar */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); setOpenQuestionIndex(0); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '14px',
                    border: 'none',
                    background: isActive ? '#fef2f2' : 'transparent',
                    color: isActive ? '#dc2626' : '#475569',
                    fontSize: '13.5px',
                    fontWeight: isActive ? 800 : 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Icon size={18} color={isActive ? '#dc2626' : '#64748b'} />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right Accordion Questions Container */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)', gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {currentQuestions.map((item, idx) => {
                const isOpen = openQuestionIndex === idx;
                return (
                  <div
                    key={idx}
                    style={{
                      borderBottom: idx === currentQuestions.length - 1 ? 'none' : '1px solid #f1f5f9',
                      paddingBottom: idx === currentQuestions.length - 1 ? '0' : '16px'
                    }}
                  >
                    <button
                      onClick={() => setOpenQuestionIndex(isOpen ? null : idx)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        padding: '8px 0',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        color: '#0f172a',
                        textAlign: 'left',
                        cursor: 'pointer'
                      }}
                    >
                      <span>{item.q}</span>
                      <ChevronDown
                        size={18}
                        color="#64748b"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
                      />
                    </button>

                    {isOpen && (
                      <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.7, marginTop: '8px', marginBottom: 0 }}>
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* BOTTOM STILL HAVE QUESTIONS SUPPORT BANNER */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '24px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Headphones size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>Still have questions?</h4>
              <p style={{ fontSize: '12.5px', color: '#475569', margin: 0 }}>
                Our support team is here to help you.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ background: '#ffffff', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '9999px', padding: '12px 24px', fontSize: '13px', fontWeight: 800, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span>Contact Support</span>
              <ArrowRight size={14} />
            </Link>
            <a href="mailto:Info@infraedgeredlight.co.uk" style={{ background: '#dc2626', color: '#ffffff', borderRadius: '9999px', padding: '12px 24px', fontSize: '13px', fontWeight: 800, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(220,38,38,0.25)' }}>
              <Mail size={16} />
              <span>Email Us</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FAQ;
